import { Box, SvgIcon, type SxProps, type Theme } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import gatePhoto from "../../assets/brand/gate-photo@2x.webp";
import {
  COLLAGE_HEIGHT,
  COLLAGE_OVERHANG,
  COLLAGE_WIDTH,
  getCollageScale,
} from "./gateCollageScale.js";
import { BRAND_PANEL_MAX_SCALE } from "./brandPanelSizing.js";

// The colours of Karo's gate board (its `--ink`, `--ink-3`, `--ok` tokens and
// the design system's navy and tints).
const COLLAGE = {
  ink: "#1A2340",
  ink3: "#6B7385",
  ok: "#1B7A5A",
  okTint: "#E4F3EC",
  line: "#E1E5EE",
  navy: "#001E64",
  tint: "#F0F5FC",
  tint2: "#DCE5F6",
} as const;

// `.snip`: a white app card floating over the photo.
const SNIPPET_SX = {
  position: "absolute",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  p: "12px 14px",
  bgcolor: "common.white",
  borderRadius: "12px",
  boxShadow: "0 12px 32px rgba(14, 30, 67, 0.28)",
  color: COLLAGE.ink,
  fontSize: 12,
  lineHeight: 1.35,
  letterSpacing: "normal",
} as const;

// `.snip .k`: the small uppercase kind of the card.
const KIND_SX = {
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: COLLAGE.ink3,
} as const;

// `.snip .v`: the card's value.
const VALUE_SX = { fontSize: 14, fontWeight: 500, color: COLLAGE.ink } as const;

/**
 * Karo's collage on the gate: a photo with two app snippets (a project on
 * track, a new web enquiry), built from the board's values so the snippets'
 * texts are translated. It illustrates and says nothing the page does not, so
 * it is hidden from assistive technology.
 */
export const GateCollage = ({
  fit,
  grow = false,
  sx,
}: {
  /**
   * `photo`: the photo fills the width and the project card overhangs it on
   * the right, as on the board (the brand panel's padding takes it). `all`:
   * the card is fitted in too, for a column without such room (phone width).
   */
  fit: "photo" | "all";
  /**
   * Grows with a wider column (the brand panel on wide screens), at most by
   * the panel's own factor: the photo is 896px wide, so even then it is drawn
   * with more than one image pixel per CSS pixel.
   */
  grow?: boolean;
  sx?: SxProps<Theme>;
}) => {
  const { t } = useTranslate(["PageSignup"]);
  const fitWidth =
    fit === "all" ? COLLAGE_WIDTH + COLLAGE_OVERHANG : COLLAGE_WIDTH;
  const maxScale = grow ? BRAND_PANEL_MAX_SCALE : 1;
  const frameRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(1);

  // The board's 448 × 300 composition is scaled as a whole to the frame's
  // width, so photo, cards and texts keep their proportions and positions and
  // nothing pushes the page wider than the screen.
  React.useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) {
      return;
    }
    const measure = () =>
      setScale(
        getCollageScale({ available: frame.clientWidth, fitWidth, maxScale })
      );
    measure();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [fitWidth, maxScale]);

  return (
    <Box
      ref={frameRef}
      aria-hidden="true"
      data-testid="gate-collage"
      sx={[
        {
          position: "relative",
          width: "100%",
          maxWidth: fitWidth * maxScale,
          aspectRatio: `${fitWidth} / ${COLLAGE_HEIGHT}`,
          flex: "none",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          width: COLLAGE_WIDTH,
          height: COLLAGE_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        <Box
          component="img"
          src={gatePhoto}
          alt=""
          sx={{
            position: "absolute",
            left: 0,
            top: 24,
            width: 448,
            height: 276,
            // Karo's photo carries its 16px rounded corners itself.
            objectFit: "cover",
          }}
        />
        <Box sx={{ ...SNIPPET_SX, right: -25, top: 98, width: 206 }}>
          <Box component="span" sx={KIND_SX}>
            {t("Project · Bathroom", { ns: "PageSignup" })}
          </Box>
          <Box component="span" sx={VALUE_SX}>
            {t("On track", { ns: "PageSignup" })}
          </Box>
          <Box
            component="span"
            sx={{
              display: "block",
              height: 6,
              borderRadius: "3px",
              bgcolor: COLLAGE.line,
              overflow: "hidden",
            }}
          >
            <Box
              component="i"
              sx={{
                display: "block",
                height: "100%",
                width: "62%",
                bgcolor: COLLAGE.ok,
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              mt: "2px",
            }}
          >
            <Box
              sx={{
                flex: "none",
                width: 34,
                borderRadius: "6px",
                overflow: "hidden",
                border: `1px solid ${COLLAGE.tint2}`,
                textAlign: "center",
                lineHeight: 1,
              }}
            >
              <Box
                component="b"
                sx={{
                  display: "block",
                  p: "3px 0 2px",
                  bgcolor: COLLAGE.navy,
                  color: "common.white",
                  fontSize: 8,
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                {t("Oct", { ns: "PageSignup" })}
              </Box>
              <Box
                component="span"
                sx={{
                  display: "block",
                  p: "3px 0 4px",
                  fontSize: 14,
                  fontWeight: 500,
                  color: COLLAGE.ink,
                }}
              >
                14
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box
                component="strong"
                sx={{ fontSize: 12, fontWeight: 500, color: COLLAGE.ink }}
              >
                {t("Work starts in 8 days", { ns: "PageSignup" })}
              </Box>
              <Box
                component="em"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  fontStyle: "normal",
                  fontSize: 11,
                  color: COLLAGE.ok,
                }}
              >
                <SvgIcon viewBox="0 0 24 24" sx={{ width: 12, height: 12 }}>
                  <path
                    d="M5 12.5l4.5 4.5L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </SvgIcon>
                {t("Quote signed", { ns: "PageSignup" })}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ ...SNIPPET_SX, right: 196, bottom: 211, width: 224 }}>
          <Box component="span" sx={KIND_SX}>
            {t("New enquiry · web form", { ns: "PageSignup" })}
          </Box>
          <Box component="span" sx={VALUE_SX}>
            {t("Roof repair after storm", { ns: "PageSignup" })}
          </Box>
          <Box
            component="span"
            sx={{
              alignSelf: "flex-start",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              height: 22,
              px: "9px",
              borderRadius: "11px",
              bgcolor: COLLAGE.tint,
              color: COLLAGE.navy,
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            {t("New · 2 min ago", { ns: "PageSignup" })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
